import { z } from 'zod';
import { pageSchema, timestampSchema } from './common';

/**
 * Durable background work (architecture documentation section 11.3).
 *
 * The original field list tracked `status` and `next_attempt_at` but had no
 * lease, which means a worker that dies mid-job either strands the row in
 * `running` forever or lets a second worker execute it concurrently. The lease
 * fields below, claimed with SELECT ... FOR UPDATE SKIP LOCKED, close that hole:
 * a claim expires on its own, so crash recovery needs no operator action.
 */

export const jobStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  /** Retries exhausted. Terminal, and visible to operators (section 19.3). */
  'dead_letter',
  'cancelled',
]);
export type JobStatus = z.infer<typeof jobStatusSchema>;

/**
 * Every job type the worker can execute.
 *
 * This is the catalog the operations page filters by, the API validates
 * against and the OpenAPI document publishes -- so a type the worker registers
 * and this list omits is a job that exists, runs, and then cannot be described.
 * `cost.rollup_rebuild` and `cost.reconcile` were in exactly that state: the
 * handlers shipped, the enum did not, and `jobs.controller.ts` bridged the gap
 * with an unchecked `as JobType` that would have handed the browser a value
 * outside its own union.
 *
 * `apps/worker/src/handlers.test.ts` asserts this list and the handler registry
 * are the same set, in both directions.
 */
export const JOB_TYPES = [
  'infrastructure.poll_service',
  'infrastructure.prune_samples',
  'audit.retention_sweep',
  'integration.sync',
  'webhook.process',
  'automation.run',
  'integration.prune_deliveries',
  'integration.reencrypt_credentials',
  'cost.rollup_rebuild',
  'cost.reconcile',
  'cost.source_sync',
  'platform.prune_expired_sessions',
  /** Deletes an asset (storage object and row) once it is neither claimed by
   *  a live anchor nor referenced from any body reachable from one --
   *  docs/rich-text-design.md section 5.5. */
  'asset.prune_orphaned',
  /** Delivers one outbound notification, and records what became of it
   *  (contracts: notifications.ts). */
  'notification.deliver',
  /** Deletes settled notification deliveries past their retention window. */
  'notification.prune_deliveries',
  /** The weekly nudge about work that is running and unreported. Runs daily and
   *  decides for itself whose reporting day it is
   *  (docs/schedule-progress-design.md section 12). */
  'schedule.progress_digest',
] as const;
export const jobTypeSchema = z.enum(JOB_TYPES);
export type JobType = z.infer<typeof jobTypeSchema>;

export const jobSchema = z.object({
  id: z.string().uuid(),
  jobType: jobTypeSchema,
  status: jobStatusSchema,
  priority: z.number().int(),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  nextAttemptAt: timestampSchema.nullable(),
  lockedBy: z.string().nullable(),
  lockExpiresAt: timestampSchema.nullable(),
  correlationId: z.string().nullable(),
  /** Machine-readable; never the raw provider message, which may carry secrets. */
  lastErrorCode: z.string().nullable(),
  lastErrorSummary: z.string().nullable(),
  createdAt: timestampSchema,
  startedAt: timestampSchema.nullable(),
  completedAt: timestampSchema.nullable(),
});
export type Job = z.infer<typeof jobSchema>;

export const jobPageSchema = pageSchema(jobSchema);

export const jobListQuerySchema = z.object({
  status: jobStatusSchema.optional(),
  jobType: jobTypeSchema.optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** Retry is an explicit, audited operator action (section 19.3). */
export const retryJobSchema = z.object({ reason: z.string().max(500).optional() }).strict();

export const queueHealthSchema = z.object({
  pending: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  deadLettered: z.number().int().nonnegative(),
  /** Age of the oldest pending job. The signal that matters for queue-depth alerts. */
  oldestPendingAgeSeconds: z.number().int().nonnegative().nullable(),
});
export type QueueHealth = z.infer<typeof queueHealthSchema>;

/**
 * Retry backoff with full jitter. Deterministic in shape, random in timing, so
 * a provider outage does not produce a synchronized retry stampede when it
 * recovers (section 11.2).
 */
export function computeBackoffMs(
  attempt: number,
  options: { baseMs?: number; maxMs?: number; random?: () => number } = {},
): number {
  const base = options.baseMs ?? 1_000;
  const max = options.maxMs ?? 5 * 60_000;
  const random = options.random ?? Math.random;
  const exponential = Math.min(max, base * 2 ** Math.max(0, attempt - 1));
  return Math.floor(random() * exponential);
}
